<script setup lang="ts">
import {ref} from 'vue';
import router from "@/checkout/router.ts";
import {useBasketStore} from "@/shared/stores/basket/basket.store.ts";
import {CheckoutConfigInput} from "@/shared/graphql/types";
import {getDomainWithoutPort} from "@/shared/composable/url.ts";
import {generateUUID} from "@/shared/composable/utils.ts";
import {ClientProductVariant, initGuestBasket} from "@/shared/stores/basket";

const basket = useBasketStore();

// Reactive variables for product code input and the list of codes
const productCode = ref('');
const id = generateUUID()
console.log(`Guest basket initialized with id:${id}`)
initGuestBasket(id)

// Function to add a product code to the basket
const addToBasket = async () => {
  const code = productCode.value.trim();
  if (code) {
    console.log(`Adding product code: ${code}`);
    const variant = {
      code: code,
      name: code,
    } as ClientProductVariant
    await basket.addVariants([{variant: variant, quantity: 1}])
    productCode.value = '';
  }
};

// Renamed from createGuestBasket to checkout
const checkout = async () => {
  try {
    const basePath = ":3001" + process.env.APP_BASE_PATH;
    const url = getDomainWithoutPort(document.location.href);

    const response = await basket.checkout({
      requester: 'Marcando-Test-Guest',
      basketReturn: {
        url: `${url}${basePath}guest-starter`,
        label: `Terug naar het winkelmandje`
      },
      completedReturn: {
        url: `${url}${basePath}guest-starter`,
        label: `Nieuwe guest basket`
      },
    } as CheckoutConfigInput);
    if (response.validation.success) {
      await router.push({name: 'checkout', params: {checkoutId: response.checkout.id}});
    } else {
      const errorMessages: string[] = [];

      if (response.validation.globalErrors && response.validation.globalErrors.length > 0) {
        response.validation.globalErrors.forEach((error) => {
          errorMessages.push(`Global error: ${error.message}`);
        });
      }

      if (response.validation.lineItemErrors && response.validation.lineItemErrors.length > 0) {
        response.validation.lineItemErrors.forEach((error) => {
          errorMessages.push(`Line item error${error.field ? ` (field: ${error.field})` : ''}: ${error.message}`);
        });
      }

      alert(errorMessages.join('\n'));

    }
  } catch (e) {
    console.error(e);
  }
};
</script>

<template>
  <div class="flex flex-col items-center justify-center min-h-screen bg-gray-100 p-4">
    <h1 class="text-4xl font-bold mb-8">Guest Basket Creation Test</h1>

    <!-- Input Section -->
    <div class="w-full max-w-md mb-6">
      <div class="flex">
        <input
            v-model="productCode"
            type="text"
            placeholder="Enter product code"
            class="flex-1 px-4 py-2 border border-gray-300 rounded-l-md focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
        <button
            @click="addToBasket"
            class="px-4 py-2 bg-blue-500 text-white rounded-r-md hover:bg-blue-600 transition duration-300"
        >
          Add
        </button>
      </div>
    </div>

    <!-- List of Added Codes -->
    <div class="w-full max-w-md mb-8">
      <h2 class="text-2xl font-semibold mb-4">Added Product Codes:</h2>
      <ul class="list-disc list-inside">
        <li v-for="(item, index) in basket.items" :key="index" class="text-gray-700">
          {{ item.name }}
        </li>
        <li v-if="basket.items.length === 0" class="text-slate-500">No products added yet.</li>
      </ul>
    </div>

    <!-- Checkout Button -->
    <button
        @click="checkout"
        class="px-6 py-3 bg-green-500 text-white rounded-lg hover:bg-green-600 transition duration-300 text-lg"
    >
      Checkout
    </button>
  </div>
</template>

<style scoped>
/* You can add additional scoped styles here if needed */
</style>